import { Loader2, AlertTriangle, RotateCcw, Mic2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRetryTranscription } from "@/lib/rehearsalQueries";
import { toast } from "sonner";

const REASON_LABELS = {
  duration_below_minimum: "Recording is shorter than the minimum measurable length",
  word_count_below_minimum: "Too few recognized words to measure reliably",
  unsupported_language: "Detected language isn't supported yet (English only, for now)",
  language_not_detected: "Couldn't detect the spoken language",
  no_speech_segments: "No speech was detected in this recording",
};

/**
 * Master v5 Prompt 24 — the deterministic speech-evidence panel: strictly
 * Measurement only. No wording here ever implies confidence, anxiety,
 * competence, personality or leadership quality — only what was
 * literally measured, and an honest "not enough evidence" note when the
 * reliability rules don't pass.
 */
export default function RehearsalMetricsPanel({ workspaceId, reh_id, job, transcript, metrics, poll }) {
  const retry = useRetryTranscription(workspaceId, reh_id);

  async function handleRetry() {
    try {
      await retry.mutateAsync();
    } catch (err) {
      toast.error(err?.message || "Retry failed");
    }
  }

  if (!job || job.status === "queued" || job.status === "processing" || poll) {
    return (
      <div className="flex items-center gap-2 text-ink-soft text-sm" data-testid="rehearsal-transcription-pending">
        <Loader2 size={14} className="animate-spin" /> Transcribing your recording…
      </div>
    );
  }

  if (job.status === "failed") {
    return (
      <div className="flex items-center justify-between gap-3 bg-terracotta/5 border border-terracotta/20 rounded-lg p-3" data-testid="rehearsal-transcription-failed">
        <p className="text-sm text-ink flex items-center gap-1.5"><AlertTriangle size={14} className="text-terracotta" /> Transcription failed: {job.last_error || "unknown error"}</p>
        <Button size="sm" variant="outline" onClick={handleRetry} disabled={retry.isPending} data-testid="rehearsal-transcription-retry-btn">
          {retry.isPending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <RotateCcw size={13} className="mr-1.5" />} Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="rehearsal-metrics-panel">
      {metrics?.reliability_status === "valid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="rehearsal-metrics-valid">
          <Metric label="Duration" value={`${Math.round(metrics.duration_seconds)}s`} />
          <Metric label="Words per minute" value={Math.round(metrics.overall_wpm)} />
          <Metric label="Pauses" value={metrics.pause_count} />
          <Metric label="Pause time" value={`${metrics.pause_total_duration_seconds}s`} />
          <Metric label="Filler words" value={metrics.filler_count} />
          <Metric label="Repetitions" value={metrics.repetition_count} />
        </div>
      ) : (
        <div className="bg-ink/5 rounded-lg p-3 text-sm text-ink-soft" data-testid="rehearsal-metrics-not-assessed">
          <p className="flex items-center gap-1.5 font-medium text-ink"><Mic2 size={14} /> Not enough evidence to measure this attempt reliably.</p>
          <ul className="list-disc list-inside mt-1">
            {(metrics?.reliability_reasons || []).map((r) => <li key={r}>{REASON_LABELS[r] || r}</li>)}
          </ul>
        </div>
      )}
      {transcript?.full_text && (
        <details className="text-sm" data-testid="rehearsal-transcript">
          <summary className="cursor-pointer text-ink-soft">Transcript</summary>
          <p className="mt-2 text-ink whitespace-pre-wrap">{transcript.full_text}</p>
        </details>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-ink/5 rounded-lg p-3" data-testid={`rehearsal-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-2xl font-serif text-ink">{value}</p>
      <p className="text-xs text-ink-soft">{label}</p>
    </div>
  );
}
